import React, { useEffect, useRef, useCallback, useState } from "react";
import { useCanvasToolbarState, useCanvasToolbarDispatch } from "adminportal/src/pages/ProductManagement/SavedDesign/providers/CanvasToolbarProvider";
import { FiCrosshair, FiRefreshCcw, FiSliders } from "react-icons/fi";
import { TemporaryImage } from "teamsite/src/pages/NewAiDesign/types";
import { LogoTransformModal } from "teamsite/src/pages/NewAiDesign/components/LogoTransformModal";

const SMOOTHING_QUALITY: "high" | "medium" = "medium";
// INCREASED HANDLE SIZE FOR TOUCH
const HANDLE_SIZE = 16;
const HANDLE_OFFSET = 25;
const ROTATION_SNAP = 5;
const IMAGE_LOAD_RETRIES = 3;
const IMAGE_RETRY_DELAY_MS = 350;
const OVERLAY_BACKGROUND_RETRY_INTERVAL_MS = 2500;
const OVERLAY_BACKGROUND_MAX_RETRIES = 12;

const wait = (ms: number) => new Promise<void>((resolve) => {
    window.setTimeout(resolve, ms);
});

const withCacheBust = (url: string, attempt: number) => {
    if (attempt <= 0) {
        return url;
    }
    const separator = url.includes("?") ? "&" : "?";
    return `${url}${separator}retry=${Date.now()}-${attempt}`;
};

const getOverlayCacheKey = (image: TemporaryImage) => {
    const source = image?.images_data?.webp || image?.images_data?.src || "";
    return `${String(image.id ?? "no-id")}::${source}`;
};

const loadImageFromCandidates = async (
    candidates: string[],
    retriesPerSource = IMAGE_LOAD_RETRIES,
    retryDelayMs = IMAGE_RETRY_DELAY_MS,
) => {
    const sources = Array.from(new Set(candidates.filter((candidate) => typeof candidate === "string" && candidate.trim() !== "")));
    let lastError: unknown = null;

    for (const source of sources) {
        for (let attempt = 0; attempt < retriesPerSource; attempt++) {
            try {
                const loadedImage = await new Promise<HTMLImageElement>((resolve, reject) => {
                    const image = new Image();
                    image.crossOrigin = "anonymous";
                    image.decoding = "async";
                    image.onload = () => {
                        if (typeof image.decode === "function") {
                            image.decode().catch(() => undefined).finally(() => resolve(image));
                            return;
                        }
                        resolve(image);
                    };
                    image.onerror = (error) => reject(error);
                    image.src = withCacheBust(source, attempt);
                });
                return loadedImage;
            } catch (error) {
                lastError = error;
                if (attempt < retriesPerSource - 1) {
                    await wait(retryDelayMs * (attempt + 1));
                }
            }
        }
    }

    throw lastError ?? new Error("Failed to load image.");
};

export const LogoEditor2D = ({
    disabled,
    panel_id,
    panel_image,
    panel_images,
    selectedImageId,
    onSelectImage,
    maxCssWidth = 800,
    aspect = "4/3",
    background = "#ffffff",
    modalAnchorRef,
    onLiveImageUpdate,
    onInteractionEnd,
}: {
    disabled: boolean;
    panel_id: number;
    panel_image: string;
    panel_images: TemporaryImage[];
    selectedImageId: number | null;
    onSelectImage: (id: number | null) => void;
    maxCssWidth?: number;
    aspect?: `${number}/${number}`;
    background?: string;
    modalAnchorRef?: React.RefObject<HTMLElement | null>;
    onLiveImageUpdate?: (image_id: number, properties: Partial<TemporaryImage>) => void;
    onInteractionEnd?: () => void;
}) => {
    const containerRef = useRef<HTMLDivElement | null>(null);

    // Canvases
    const bgCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const baseCanvasRef = useRef<HTMLCanvasElement | null>(null);
    const overlayCanvasRef = useRef<HTMLCanvasElement | null>(null);

    const imgRef = useRef<HTMLImageElement | null>(null);
    const [isPanelLoading, setIsPanelLoading] = useState(false);
    // Safe default in case context is missing
    const toolbarState = useCanvasToolbarState();
    const { zoom = 1, pan = { x: 0, y: 0 }, panEnabled = false } = toolbarState || {};

    const toolbarDispatch = useCanvasToolbarDispatch();
    const zoomIn = toolbarDispatch?.zoomIn || (() => { });
    const zoomOut = toolbarDispatch?.zoomOut || (() => { });
    const setPan = toolbarDispatch?.setPan || (() => { });
    const setZoom = toolbarDispatch?.setZoom || (() => { });
    const resetZoom = toolbarDispatch?.resetZoom || (() => { });
    const resetPan = toolbarDispatch?.resetPan || (() => { });

    const imageCache = useRef(new Map<string, HTMLImageElement>());
    const pendingOverlaySourcesRef = useRef(new Map<string, string[]>());
    const pendingOverlayRetryCountRef = useRef(new Map<string, number>());
    const pendingOverlayInFlightRef = useRef(new Set<string>());

    const panRef = useRef(pan);
    const zoomRef = useRef(zoom);

    const localImagesRef = useRef<TemporaryImage[]>(panel_images || []);
    const selectedImageIdRef = useRef<number | null>(selectedImageId);
    const dirtyImageIdsRef = useRef<Set<number>>(new Set());

    // FIX: Allow string or number keys for robustness
    const overlayRectsRef = useRef<Map<string | number, { x: number; y: number; w: number; h: number, rotation: number }>>(new Map());

    const rafDrawIdRef = useRef<number | null>(null);
    const rafOverlayIdRef = useRef<number | null>(null);
    const baseLoadedRef = useRef(false);
    const expectedOverlaysRef = useRef(0);
    const loadedOverlaysRef = useRef(0);
    const initialDrawDoneRef = useRef(false);

    const draggingRef = useRef(false);
    const lastRef = useRef<{ x: number; y: number } | null>(null);
    const activeInteractionRef = useRef<{
        type: "none" | "pan" | "move" | "rotate" | "scale";
        targetId: number | null;
        startVal: number;
        startMouse: { x: number, y: number };
        centerX: number;
        centerY: number;
    }>({
        type: "none", targetId: null, startVal: 0, startMouse: { x: 0, y: 0 }, centerX: 0, centerY: 0
    });

    const source = panel_image;
    const [showGuides, setShowGuides] = useState(false);
    const [isTransformOpen, setIsTransformOpen] = useState(false);



    useEffect(() => {
        baseLoadedRef.current = false;
        expectedOverlaysRef.current = 0;
        loadedOverlaysRef.current = 0;
        initialDrawDoneRef.current = false;
    }, [panel_id, source]);

    // --- UTILS ---

    const resizeCanvasToContainer = (canvas: HTMLCanvasElement, container: HTMLDivElement) => {
        const rect = container.getBoundingClientRect();
        const cssW = Math.max(1, Math.round(rect.width));
        const cssH = Math.max(1, Math.round(rect.height));
        const dpr = Math.max(1, window.devicePixelRatio || 1);

        const targetW = Math.round(cssW * dpr);
        const targetH = Math.round(cssH * dpr);

        if (canvas.width !== targetW || canvas.height !== targetH) {
            canvas.width = targetW;
            canvas.height = targetH;
        }

        return { cssW, cssH, dpr };
    };

    const computeTransform = (cssW: number, cssH: number, img: HTMLImageElement) => {
        const z = Math.max(0.01, zoomRef.current || 1);
        const baseScale = Math.min(cssW / img.naturalWidth, cssH / img.naturalHeight);
        const drawW = img.naturalWidth * baseScale * z;
        const drawH = img.naturalHeight * baseScale * z;
        const p = panRef.current || { x: 0, y: 0 };
        const dx = (cssW - drawW) / 2 + p.x;
        const dy = (cssH - drawH) / 2 + p.y;
        return { baseScale, drawW, drawH, dx, dy, z };
    };

    // --- DRAWING ---

    const drawBackground = useCallback(() => {
        const container = containerRef.current;
        const canvas = bgCanvasRef.current;
        if (!container || !canvas) return;
        const { cssW, cssH, dpr } = resizeCanvasToContainer(canvas, container);
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cssW, cssH);
        ctx.fillStyle = background;
        ctx.fillRect(0, 0, cssW, cssH);
        ctx.strokeStyle = "#e5e7eb";
        ctx.lineWidth = 1;
        ctx.strokeRect(0.5, 0.5, cssW - 1, cssH - 1);
    }, [background]);

    const drawBasePanel = useCallback(() => {
        const container = containerRef.current;
        const canvas = baseCanvasRef.current;
        const img = imgRef.current;
        if (!container || !canvas || !img) return;
        const { cssW, cssH, dpr } = resizeCanvasToContainer(canvas, container);
        const ctx = canvas.getContext("2d");
        if (!ctx) return;
        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cssW, cssH);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = SMOOTHING_QUALITY;
        const { drawW, drawH, dx, dy } = computeTransform(cssW, cssH, img);
        ctx.drawImage(img, dx, dy, drawW, drawH);
    }, []);

    const drawControlHandles = (
        ctx: CanvasRenderingContext2D,
        cx: number, cy: number, w: number, h: number, rotation: number
    ) => {
        ctx.save();
        ctx.translate(cx, cy);
        ctx.rotate((rotation * Math.PI) / 180);

        const hw = w / 2;
        const hh = h / 2;

        // Boundary
        ctx.strokeStyle = "#FA5500";
        ctx.lineWidth = 1;
        ctx.strokeRect(-hw, -hh, w, h);

        // Scale Handles
        ctx.fillStyle = "#ffffff";
        ctx.strokeStyle = "#FA5500";
        ctx.lineWidth = 2;
        const corners = [
            { x: -hw, y: -hh }, { x: hw, y: -hh },
            { x: hw, y: hh }, { x: -hw, y: hh }
        ];

        corners.forEach(c => {
            ctx.fillRect(c.x - HANDLE_SIZE / 2, c.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
            ctx.strokeRect(c.x - HANDLE_SIZE / 2, c.y - HANDLE_SIZE / 2, HANDLE_SIZE, HANDLE_SIZE);
        });

        // Rotation Handle
        const rotY = -hh - HANDLE_OFFSET;
        ctx.beginPath();
        ctx.moveTo(0, -hh);
        ctx.lineTo(0, rotY);
        ctx.stroke();

        ctx.beginPath();
        ctx.arc(0, rotY, HANDLE_SIZE / 2 + 1, 0, 2 * Math.PI);
        ctx.fill();
        ctx.stroke();

        ctx.restore();
    };

    const drawOverlays = useCallback(() => {
        const container = containerRef.current;
        const canvas = overlayCanvasRef.current;
        const img = imgRef.current;
        if (!container || !canvas || !img) return;

        const { cssW, cssH, dpr } = resizeCanvasToContainer(canvas, container);
        const ctx = canvas.getContext("2d");
        if (!ctx) return;

        ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
        ctx.clearRect(0, 0, cssW, cssH);
        ctx.imageSmoothingEnabled = true;
        ctx.imageSmoothingQuality = SMOOTHING_QUALITY;

        const { baseScale, drawW, drawH, dx, dy, z } = computeTransform(cssW, cssH, img);

        overlayRectsRef.current.clear();
        const currentImages = localImagesRef.current || [];

        for (const overlay of currentImages) {
            const key = getOverlayCacheKey(overlay);
            const oimg = imageCache.current.get(key);
            if (!oimg) continue;

            const isSelected = selectedImageIdRef.current != null && overlay.id === selectedImageIdRef.current;
            const zf = Math.max(0.01, z);
            const baseOverlayScale = overlay.scale && overlay.scale > 0 ? overlay.scale : 1;
            const oScale = baseOverlayScale * baseScale * zf;

            const ow = oimg.naturalWidth * oScale;
            const oh = oimg.naturalHeight * oScale;

            const ox = dx + (overlay.x_position ?? 0) * baseScale * zf;
            const oy = dy + (overlay.y_position ?? 0) * baseScale * zf;

            if (overlay.id != null) {
                overlayRectsRef.current.set(overlay.id, {
                    x: ox,
                    y: oy,
                    w: ow,
                    h: oh,
                    rotation: overlay.rotation || 0
                });
            }

            ctx.save();
            const cx = ox + ow / 2;
            const cy = oy + oh / 2;

            if (overlay.rotation && overlay.rotation !== 0) {
                ctx.translate(cx, cy);
                ctx.rotate((overlay.rotation * Math.PI) / 180);
                ctx.drawImage(oimg, -ow / 2, -oh / 2, ow, oh);
            } else {
                ctx.drawImage(oimg, ox, oy, ow, oh);
            }
            ctx.restore();

            if (isSelected) {
                drawControlHandles(ctx, cx, cy, ow, oh, overlay.rotation || 0);
            }
        }

        if (showGuides) {
            ctx.save();
            ctx.strokeStyle = "rgba(249, 22, 166, 0.5)";
            ctx.lineWidth = 2; // Reduced from 10 (10 is very thick!)
            ctx.setLineDash([6, 6]);
            ctx.beginPath();

            // Keep guide center anchored to the panel image bounds, not the viewport pan position.
            const panelCenterX = dx + drawW / 2;
            const panelCenterY = dy + drawH / 2;
            const panelLeft = dx;
            const panelRight = dx + drawW;
            const panelTop = dy;
            const panelBottom = dy + drawH;

            // Vertical Line (center of panel image)
            ctx.moveTo(panelCenterX, panelTop);
            ctx.lineTo(panelCenterX, panelBottom);

            // Horizontal Line (center of panel image)
            ctx.moveTo(panelLeft, panelCenterY);
            ctx.lineTo(panelRight, panelCenterY);

            ctx.stroke();
            ctx.restore();
        }
    }, [showGuides]);

    const scheduleDraw = useCallback(() => {
        if (rafDrawIdRef.current != null) return;
        rafDrawIdRef.current = requestAnimationFrame(() => {
            rafDrawIdRef.current = null;
            drawBackground();
            drawBasePanel();
            drawOverlays();
        });
    }, [drawBackground, drawBasePanel, drawOverlays]);

    // --- HIT TESTING ---

    const unrotatePoint = (px: number, py: number, cx: number, cy: number, angleDeg: number) => {
        const rad = (-angleDeg * Math.PI) / 180;
        const dx = px - cx;
        const dy = py - cy;
        return {
            x: cx + dx * Math.cos(rad) - dy * Math.sin(rad),
            y: cy + dx * Math.sin(rad) + dy * Math.cos(rad)
        };
    };

    const getHandleHit = (mx: number, my: number, rect: { x: number, y: number, w: number, h: number, rotation: number }) => {
        const cx = rect.x + rect.w / 2;
        const cy = rect.y + rect.h / 2;
        const p = unrotatePoint(mx, my, cx, cy, rect.rotation);

        const hw = rect.w / 2;
        const hh = rect.h / 2;
        const hs = HANDLE_SIZE / 2 + 6; // Hit tolerance added for touch

        // Rotation Handle
        const rotY = -hh - HANDLE_OFFSET;
        const dxRot = p.x - cx;
        const dyRot = p.y - (cy + rotY);
        // Circular hit test for rotation handle
        if (dxRot * dxRot + dyRot * dyRot <= (HANDLE_SIZE) * (HANDLE_SIZE)) {
            return "rotate";
        }

        // Corner Handles
        if (Math.abs(p.x - (cx - hw)) <= hs && Math.abs(p.y - (cy - hh)) <= hs) return "scale-tl";
        if (Math.abs(p.x - (cx + hw)) <= hs && Math.abs(p.y - (cy - hh)) <= hs) return "scale-tr";
        if (Math.abs(p.x - (cx + hw)) <= hs && Math.abs(p.y - (cy + hh)) <= hs) return "scale-br";
        if (Math.abs(p.x - (cx - hw)) <= hs && Math.abs(p.y - (cy + hh)) <= hs) return "scale-bl";

        return null;
    };

    const hitTestOverlay = (x: number, y: number) => {
        // FIX: Relaxed filtering to allow string IDs and handle types safely
        const ids = (localImagesRef.current || [])
            .map(im => im.id)
            .filter((id) => id != null);

        // Reverse check (top-most first)
        for (let i = ids.length - 1; i >= 0; i--) {
            const id = ids[i];
            if (id == null) continue;

            const rect = overlayRectsRef.current.get(id);
            if (!rect) continue;

            const cx = rect.x + rect.w / 2;
            const cy = rect.y + rect.h / 2;
            const p = unrotatePoint(x, y, cx, cy, rect.rotation);

            if (p.x >= rect.x && p.x <= rect.x + rect.w && p.y >= rect.y && p.y <= rect.y + rect.h) {
                return id;
            }
        }
        return null;
    };

    // --- EVENTS ---

    const onPointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (disabled) return;
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const x = e.clientX - rect.left;
        const y = e.clientY - rect.top;

        // 1. Check controls of Selected Image
        if (selectedImageIdRef.current != null) {
            const r = overlayRectsRef.current.get(selectedImageIdRef.current);
            if (r) {
                const handle = getHandleHit(x, y, r);
                if (handle) {
                    const cx = r.x + r.w / 2;
                    const cy = r.y + r.h / 2;
                    const img = localImagesRef.current.find(i => i.id === selectedImageIdRef.current);

                    draggingRef.current = true;
                    (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);

                    if (handle === "rotate") {
                        activeInteractionRef.current = {
                            type: "rotate",
                            targetId: selectedImageIdRef.current,
                            startVal: img?.rotation || 0,
                            startMouse: { x, y },
                            centerX: cx, centerY: cy
                        };
                    } else {
                        activeInteractionRef.current = {
                            type: "scale",
                            targetId: selectedImageIdRef.current,
                            startVal: img?.scale || 1.0,
                            startMouse: { x, y },
                            centerX: cx, centerY: cy
                        };
                    }
                    e.preventDefault();
                    return;
                }
            }
        }

        // 2. Check Body Hit
        const hitId = hitTestOverlay(x, y);

        if (hitId != null && e.button === 0 && !e.altKey) {
            // FIX: Ensure ID is number if that's what props expect, but handle strings if present
            const numericId = Number(hitId);
            const safeId = isNaN(numericId) ? (hitId as unknown as number) : numericId;

            selectedImageIdRef.current = safeId;
            onSelectImage(safeId);

            const r = overlayRectsRef.current.get(hitId)!;
            const cx = r.x + r.w / 2;
            const cy = r.y + r.h / 2;

            draggingRef.current = true;
            activeInteractionRef.current = {
                type: "move",
                targetId: safeId,
                startVal: 0,
                startMouse: { x, y },
                centerX: cx, centerY: cy
            };
            lastRef.current = { x: e.clientX, y: e.clientY };
            (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
            scheduleDraw();
            e.preventDefault();
            return;
        }

        // 3. Pan / Deselect
        if (e.button === 0 || e.button === 1) {
            if (selectedImageIdRef.current != null) {
                selectedImageIdRef.current = null;
                onSelectImage(null);
                scheduleDraw();
            }
            if (panEnabled || e.altKey || e.button === 1) {
                draggingRef.current = true;
                activeInteractionRef.current = { type: "pan", targetId: null, startVal: 0, startMouse: { x, y }, centerX: 0, centerY: 0 };
                lastRef.current = { x: e.clientX, y: e.clientY };
                (e.currentTarget as HTMLCanvasElement).setPointerCapture(e.pointerId);
                e.preventDefault();
            }
        }
    };

    const onPointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (disabled) return;
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;
        const rect = canvas.getBoundingClientRect();
        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        if (!draggingRef.current) {
            let cursor = "default";
            if (panEnabled) cursor = "grab";
            else if (selectedImageIdRef.current != null) {
                const r = overlayRectsRef.current.get(selectedImageIdRef.current);
                if (r) {
                    const handle = getHandleHit(mx, my, r);
                    if (handle === "rotate") cursor = "grab";
                    else if (handle?.startsWith("scale")) cursor = "crosshair";
                    else {
                        const p = unrotatePoint(mx, my, r.x + r.w / 2, r.y + r.h / 2, r.rotation);
                        if (p.x >= r.x && p.x <= r.x + r.w && p.y >= r.y && p.y <= r.y + r.h) cursor = "move";
                    }
                }
            } else {
                if (hitTestOverlay(mx, my) != null) cursor = "pointer";
            }
            canvas.style.cursor = cursor;
            return;
        }

        const interaction = activeInteractionRef.current;

        const getInteractionFactor = () => {
            const img = imgRef.current;
            if (containerRef.current && img) {
                const z = Math.max(0.01, zoomRef.current || 1);
                const cRect = containerRef.current.getBoundingClientRect();
                const cssW = Math.max(1, Math.round(cRect.width));
                const cssH = Math.max(1, Math.round(cRect.height));
                const baseScale = Math.min(cssW / img.naturalWidth, cssH / img.naturalHeight);
                return baseScale * z;
            }
            return 1;
        };

        if (interaction.type === "rotate" && interaction.targetId != null) {
            const cx = interaction.centerX;
            const cy = interaction.centerY;
            const angleRad = Math.atan2(my - cy, mx - cx);
            let angleDeg = angleRad * (180 / Math.PI);
            angleDeg += 90;

            if (Math.abs(angleDeg) < ROTATION_SNAP) angleDeg = 0;
            if (Math.abs(angleDeg - 90) < ROTATION_SNAP) angleDeg = 90;
            if (Math.abs(angleDeg - 180) < ROTATION_SNAP) angleDeg = 180;
            if (Math.abs(angleDeg - 270) < ROTATION_SNAP) angleDeg = 270;
            if (Math.abs(angleDeg - 360) < ROTATION_SNAP) angleDeg = 0;

            const nextImages = localImagesRef.current.map(im =>
                im.id === interaction.targetId ? { ...im, rotation: angleDeg } : im
            );
            localImagesRef.current = nextImages;

            if (onLiveImageUpdate) onLiveImageUpdate(interaction.targetId, { rotation: angleDeg });
            scheduleDraw();
            return;
        }

        if (interaction.type === "scale" && interaction.targetId != null) {
            const cx = interaction.centerX;
            const cy = interaction.centerY;
            const startDist = Math.hypot(interaction.startMouse.x - cx, interaction.startMouse.y - cy);
            const curDist = Math.hypot(mx - cx, my - cy);

            if (startDist > 0) {
                const ratio = curDist / startDist;
                const newScale = interaction.startVal * ratio;
                const safeScale = Math.max(0.05, newScale);

                const nextImages = localImagesRef.current.map(im =>
                    im.id === interaction.targetId ? { ...im, scale: safeScale } : im
                );
                localImagesRef.current = nextImages;
                if (onLiveImageUpdate) onLiveImageUpdate(interaction.targetId, { scale: safeScale });
                scheduleDraw();
            }
            return;
        }

        if (interaction.type === "move" && interaction.targetId != null) {
            if (!lastRef.current) return;
            const dx = e.clientX - lastRef.current.x;
            const dy = e.clientY - lastRef.current.y;
            lastRef.current = { x: e.clientX, y: e.clientY };

            const factor = getInteractionFactor();
            const imgSpaceDX = factor > 0 ? dx / factor : 0;
            const imgSpaceDY = factor > 0 ? dy / factor : 0;

            activeInteractionRef.current.centerX += dx;
            activeInteractionRef.current.centerY += dy;

            const nextImages = localImagesRef.current.map(im => {
                if (im.id === interaction.targetId) {
                    return { ...im, x_position: (im.x_position ?? 0) + imgSpaceDX, y_position: (im.y_position ?? 0) + imgSpaceDY };
                }
                return im;
            });
            localImagesRef.current = nextImages;
            if (onLiveImageUpdate) {
                const moved = nextImages.find(i => i.id === interaction.targetId);
                if (moved) onLiveImageUpdate(interaction.targetId!, { x_position: moved.x_position, y_position: moved.y_position });
            }
            scheduleDraw();
            return;
        }

        if (interaction.type === "pan") {
            if (!lastRef.current) return;
            const dx = e.clientX - lastRef.current.x;
            const dy = e.clientY - lastRef.current.y;
            lastRef.current = { x: e.clientX, y: e.clientY };
            panRef.current = { x: panRef.current.x + dx, y: panRef.current.y + dy };
            scheduleDraw();
        }
    };

    const onPointerUp = (e: React.PointerEvent<HTMLCanvasElement>) => {
        if (disabled) return;
        if (!draggingRef.current) return;
        draggingRef.current = false;
        (e.currentTarget as HTMLCanvasElement).releasePointerCapture(e.pointerId);

        if (activeInteractionRef.current.type !== "pan" && activeInteractionRef.current.type !== "none") {
            if (onInteractionEnd) onInteractionEnd();
        }
        if (activeInteractionRef.current.type === "pan") {
            setPan(panRef.current);
        }

        activeInteractionRef.current = { type: "none", targetId: null, startVal: 0, startMouse: { x: 0, y: 0 }, centerX: 0, centerY: 0 };
        e.preventDefault();
    };

    const onWheel = useCallback(

        (event: WheelEvent) => {
            if (disabled) return;
            if (event.ctrlKey) return;
            event.preventDefault();
            event.stopPropagation();
            if (event.deltaY < 0) {
                zoomIn();
            } else if (event.deltaY > 0) {
                zoomOut();
            }
        },
        [zoomIn, zoomOut]
    );

    // --- EFFECTS ---

    useEffect(() => {
        if (disabled) return;
        const canvas = overlayCanvasRef.current;
        if (!canvas) return;
        canvas.addEventListener("wheel", onWheel, { passive: false });
        return () => canvas.removeEventListener("wheel", onWheel);
    }, [onWheel]);

    useEffect(() => {
        if (disabled) return;
        panRef.current = pan;
        scheduleDraw();
    }, [pan, scheduleDraw]);

    useEffect(() => {
        if (disabled) return;
        zoomRef.current = zoom;
        scheduleDraw();
    }, [zoom, scheduleDraw]);

    useEffect(() => {
        if (disabled) return;
        if (draggingRef.current) return;
        const nextImages = (panel_images || []).map((image) => {
            if (image.id == null) return image;
            if (!dirtyImageIdsRef.current.has(image.id)) return image;
            const localImage = localImagesRef.current.find((im) => im.id === image.id);
            if (!localImage) {
                dirtyImageIdsRef.current.delete(image.id);
                return image;
            }
            const matchesLocal =
                (image.x_position ?? 0) === (localImage.x_position ?? 0) &&
                (image.y_position ?? 0) === (localImage.y_position ?? 0) &&
                (image.scale ?? 1) === (localImage.scale ?? 1) &&
                (image.rotation ?? 0) === (localImage.rotation ?? 0);
            if (matchesLocal) {
                dirtyImageIdsRef.current.delete(image.id);
                return image;
            }
            return localImage;
        });
        localImagesRef.current = nextImages;
        selectedImageIdRef.current = selectedImageId;
        scheduleDraw();
    }, [panel_id, panel_images, selectedImageId, scheduleDraw]);

    useEffect(() => {
        scheduleDraw();
    }, [showGuides, scheduleDraw]);

    const draw = useCallback(() => {
        scheduleDraw();
    }, [scheduleDraw]);

    const maybeInitialDraw = useCallback(() => {
        if (
            !initialDrawDoneRef.current &&
            baseLoadedRef.current &&
            loadedOverlaysRef.current >= expectedOverlaysRef.current
        ) {
            initialDrawDoneRef.current = true;
            draw();
        }
    }, [draw]);

    useEffect(() => {
        if (disabled) return;
        if (!source) return;
        let cancelled = false;
        baseLoadedRef.current = false;
        initialDrawDoneRef.current = false;
        setIsPanelLoading(true);
        const cached = imageCache.current.get(source);
        if (cached) {
            imgRef.current = cached;
            baseLoadedRef.current = true;
            setIsPanelLoading(false);
            maybeInitialDraw();
        } else {
            loadImageFromCandidates([source], IMAGE_LOAD_RETRIES, IMAGE_RETRY_DELAY_MS)
                .then((loadedImage) => {
                    if (cancelled) return;
                    imgRef.current = loadedImage;
                    imageCache.current.set(source, loadedImage);
                    baseLoadedRef.current = true;
                    setIsPanelLoading(false);
                    maybeInitialDraw();
                })
                .catch(() => {
                    if (cancelled) return;
                    imgRef.current = null;
                    baseLoadedRef.current = true;
                    setIsPanelLoading(false);
                    maybeInitialDraw();
                });
        }
        return () => {
            cancelled = true;
        };
    }, [source, maybeInitialDraw]);

    useEffect(() => {
        if (disabled) return;
        let cancelled = false;
        const imgs = panel_images || [];
        const overlaysWithSrc = imgs.filter((ov) => ov?.images_data?.webp || ov.images_data?.src);
        const activeOverlayKeys = new Set(overlaysWithSrc.map(getOverlayCacheKey));

        expectedOverlaysRef.current = overlaysWithSrc.length;
        loadedOverlaysRef.current = 0;
        initialDrawDoneRef.current = false;

        // Drop stale retry queues when overlays change.
        for (const key of Array.from(pendingOverlaySourcesRef.current.keys())) {
            if (!activeOverlayKeys.has(key)) {
                pendingOverlaySourcesRef.current.delete(key);
                pendingOverlayRetryCountRef.current.delete(key);
                pendingOverlayInFlightRef.current.delete(key);
            }
        }

        const markOverlayComplete = (shouldRedraw: boolean) => {
            loadedOverlaysRef.current += 1;
            if (!initialDrawDoneRef.current) {
                maybeInitialDraw();
            } else if (shouldRedraw) {
                draw();
            }
        };

        overlaysWithSrc.forEach((ov) => {
            const key = getOverlayCacheKey(ov);
            const sourceCandidates = [
                ov?.images_data?.webp || "",
                ov?.images_data?.src || "",
            ];

            const cached = imageCache.current.get(key);
            if (cached) {
                pendingOverlaySourcesRef.current.delete(key);
                pendingOverlayRetryCountRef.current.delete(key);
                pendingOverlayInFlightRef.current.delete(key);
                markOverlayComplete(false);
                return;
            }

            loadImageFromCandidates(sourceCandidates, IMAGE_LOAD_RETRIES, IMAGE_RETRY_DELAY_MS)
                .then((loadedImage) => {
                    if (cancelled) return;
                    imageCache.current.set(key, loadedImage);
                    pendingOverlaySourcesRef.current.delete(key);
                    pendingOverlayRetryCountRef.current.delete(key);
                    pendingOverlayInFlightRef.current.delete(key);
                    markOverlayComplete(true);
                })
                .catch(() => {
                    if (cancelled) return;
                    pendingOverlaySourcesRef.current.set(key, sourceCandidates);
                    if (!pendingOverlayRetryCountRef.current.has(key)) {
                        pendingOverlayRetryCountRef.current.set(key, 0);
                    }
                    markOverlayComplete(false);
                });
        });

        if (overlaysWithSrc.length === 0) {
            loadedOverlaysRef.current = 0;
            maybeInitialDraw();
        }
        return () => {
            cancelled = true;
        };
    }, [panel_images, maybeInitialDraw, draw]);

    useEffect(() => {
        if (disabled) return;
        let cancelled = false;

        const intervalId = window.setInterval(() => {
            if (cancelled) return;
            const pendingEntries = Array.from(pendingOverlaySourcesRef.current.entries());
            if (pendingEntries.length === 0) return;

            pendingEntries.forEach(([key, sourceCandidates]) => {
                if (imageCache.current.has(key)) {
                    pendingOverlaySourcesRef.current.delete(key);
                    pendingOverlayRetryCountRef.current.delete(key);
                    pendingOverlayInFlightRef.current.delete(key);
                    return;
                }
                if (pendingOverlayInFlightRef.current.has(key)) {
                    return;
                }

                const attemptCount = pendingOverlayRetryCountRef.current.get(key) || 0;
                if (attemptCount >= OVERLAY_BACKGROUND_MAX_RETRIES) {
                    return;
                }

                pendingOverlayInFlightRef.current.add(key);
                pendingOverlayRetryCountRef.current.set(key, attemptCount + 1);

                loadImageFromCandidates(sourceCandidates, 1, IMAGE_RETRY_DELAY_MS)
                    .then((loadedImage) => {
                        pendingOverlayInFlightRef.current.delete(key);
                        if (cancelled) return;
                        imageCache.current.set(key, loadedImage);
                        pendingOverlaySourcesRef.current.delete(key);
                        pendingOverlayRetryCountRef.current.delete(key);
                        draw();
                    })
                    .catch(() => {
                        pendingOverlayInFlightRef.current.delete(key);
                    });
            });
        }, OVERLAY_BACKGROUND_RETRY_INTERVAL_MS);

        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [disabled, draw]);

    useEffect(() => {
        if (disabled) return;
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => draw());
        ro.observe(el);
        return () => ro.disconnect();
    }, [draw]);

    useEffect(() => {
        if (disabled) return;
        return () => {
            if (rafDrawIdRef.current != null) cancelAnimationFrame(rafDrawIdRef.current);
            if (rafOverlayIdRef.current != null) cancelAnimationFrame(rafOverlayIdRef.current);
        };
    }, []);

    const selectedImage = panel_images?.find((image) => image.id === selectedImageId) || null;

    useEffect(() => {
        if (!selectedImageId || !selectedImage) {
            setIsTransformOpen(false);
        }
    }, [selectedImageId, selectedImage]);

    if (!source) {
        return <p className="text-gray-500 text-sm">No image available for this panel.</p>;
    }

    return (
        <div
            ref={containerRef}
            className="relative w-full mx-auto bg-white rounded border shadow-inner"
            style={{ maxWidth: `${maxCssWidth}px`, aspectRatio: aspect }}
        >
            <div className="absolute right-2 top-2 z-20 flex flex-col items-center gap-2">
                <button
                    type="button"
                    onClick={() => { resetZoom(); resetPan(); }}
                    disabled={disabled}
                    className="flex h-8 w-8 items-center justify-center rounded border border-gray-200 bg-white text-gray-600 shadow-sm transition hover:border-orange-200 hover:text-orange-600"
                    title="Reset zoom"
                >
                    <FiRefreshCcw className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">Reset zoom</span>
                </button>
                <button
                    type="button"
                    onClick={() => setShowGuides((prev) => !prev)}
                    disabled={disabled}
                    className={`flex h-8 w-8 items-center justify-center rounded border shadow-sm transition ${showGuides
                            ? "border-orange-200 bg-orange-50 text-orange-600"
                            : "border-gray-200 bg-white text-gray-600 hover:border-orange-200 hover:text-orange-600"
                        }`}
                    title="Toggle guides"
                >
                    <FiCrosshair className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">Toggle guides</span>
                </button>
                <button
                    type="button"
                    onClick={() => setIsTransformOpen(!isTransformOpen)}
                    disabled={disabled || !selectedImageId}
                    className={`flex h-8 w-8 items-center justify-center rounded border shadow-sm transition ${
                        selectedImageId
                            ? "border-gray-200 bg-white text-gray-600 hover:border-orange-200 hover:text-orange-600"
                            : "cursor-not-allowed border-gray-200 bg-gray-100 text-gray-300"
                    }`}
                    title="Edit size and rotation"
                >
                    <FiSliders className="h-4 w-4" aria-hidden="true" />
                    <span className="sr-only">Edit size and rotation</span>
                </button>
            </div>
            <canvas ref={bgCanvasRef} className="absolute inset-0 w-full h-full" style={{ zIndex: 1 }} />
            <canvas ref={baseCanvasRef} className="absolute inset-0 w-full h-full" style={{ zIndex: 2, background: "transparent" }} />
            <canvas
                ref={overlayCanvasRef}
                className="absolute inset-0 w-full h-full cursor-default"
                style={{ zIndex: 3, background: "transparent", touchAction: "none" }}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
            />
            {isPanelLoading && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/70">
                    <div className="flex items-center gap-2 text-sm font-semibold text-gray-700">
                        <svg className="h-5 w-5 animate-spin text-orange-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        Loading panel image...
                    </div>
                </div>
            )}
            <LogoTransformModal
                open={isTransformOpen}
                disabled={disabled}
                image={selectedImage}
                anchorRef={modalAnchorRef ?? containerRef}
                onClose={() => setIsTransformOpen(false)}
                onUpdate={(imageId, updates) => {
                    localImagesRef.current = localImagesRef.current.map((image) =>
                        image.id === imageId ? { ...image, ...updates } : image
                    );
                    dirtyImageIdsRef.current.add(imageId);
                    onLiveImageUpdate?.(imageId, updates);
                    scheduleDraw();
                }}
            />
        </div>
    );
};
